import { useEffect, useState, useRef, useCallback } from "react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, ConfirmDialog } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Save, Plus, Trash2, Download, Upload, Building2, DatabaseBackup, History, Loader2, FileSpreadsheet, RotateCw, Mail, Send, Percent, Pencil } from "lucide-react";

export default function Parametres() {
  const [settings, setSettings] = useState(null);
  const [audit, setAudit] = useState([]);
  const [backups, setBackups] = useState([]);
  const [reminders, setReminders] = useState(null);
  const [actes, setActes] = useState([]);
  const [taux, setTaux] = useState([]);
  const [tauxDialog, setTauxDialog] = useState(false);
  const [editingTaux, setEditingTaux] = useState(null);
  const [tauxForm, setTauxForm] = useState({ acte_generateur_id: "", taux: "", code: "" });
  const [sending, setSending] = useState(false);
  const [pendingRestore, setPendingRestore] = useState(null);
  const [tab, setTab] = useState("organisme");
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);
  const impAssRef = useRef(null);
  const impNoteRef = useRef(null);

  const load = useCallback(async () => {
    const [s, a, b, ac, tx] = await Promise.all([
      api.get("/settings"),
      api.get("/audit", { params: { limit: 200 } }),
      api.get("/backups"),
      api.get("/actes"),
      api.get("/taux"),
    ]);
    setSettings(s.data);
    setAudit(a.data);
    setBackups(b.data);
    setActes(ac.data);
    setTaux(tx.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const setBanque = (i, k, v) => setSettings((s) => {
    const banques = [...(s.banques || [])];
    banques[i] = { ...banques[i], [k]: v };
    return { ...s, banques };
  });
  const addBanque = () => setSettings((s) => ({ ...s, banques: [...(s.banques || []), { banque: "", compte: "", monnaie: "CDF" }] }));
  const removeBanque = (i) => setSettings((s) => ({ ...s, banques: s.banques.filter((_, idx) => idx !== i) }));

  const saveSettings = async () => {
    try {
      const { id, ...payload } = settings;
      await api.put("/settings", payload);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const loadReminders = async () => {
    const { data } = await api.get("/reminders/preview");
    setReminders(data);
  };

  const sendReminders = async (test) => {
    setSending(true);
    try {
      const { data } = await api.post(`/reminders/run${test ? "?test=true" : ""}`);
      if (test) {
        toast.success(`Email de test envoyé (${data.sent} envoyé)`);
      } else if (data.sent === 0 && data.skipped > 0) {
        toast.warning(`Aucun email envoyé (${data.skipped} ignoré/limité). Réessayez dans une minute.`);
      } else {
        toast.success(`Relances : ${data.sent} envoyée(s), ${data.skipped} ignorée(s) sur ${data.unpaid} impayé(s)`);
      }
      loadReminders();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de l'envoi");
    }
    setSending(false);
  };

  const openTaux = (t) => {
    if (t) {
      setEditingTaux(t);
      setTauxForm({ acte_generateur_id: t.acte_generateur_id, taux: String(t.taux ?? ""), code: t.code || "" });
    } else {
      setEditingTaux(null);
      setTauxForm({ acte_generateur_id: "", taux: "", code: "" });
    }
    setTauxDialog(true);
  };

  const saveTaux = async () => {
    if (!tauxForm.acte_generateur_id) { toast.error("Sélectionnez un acte générateur"); return; }
    if (tauxForm.taux === "" || isNaN(Number(tauxForm.taux))) { toast.error("Taux invalide"); return; }
    const payload = { acte_generateur_id: tauxForm.acte_generateur_id, taux: Number(tauxForm.taux), code: tauxForm.code };
    try {
      if (editingTaux) await api.put(`/taux/${editingTaux.id}`, payload);
      else await api.post("/taux", payload);
      toast.success(editingTaux ? "Taux modifié" : "Taux ajouté");
      setTauxDialog(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteTaux = async (id) => {
    try {
      await api.delete(`/taux/${id}`);
      toast.success("Taux supprimé");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadBackup = () => window.open(`${API}/backup`, "_blank");
  const downloadStored = (id) => window.open(`${API}/backups/${id}/download`, "_blank");

  const createBackupNow = async () => {
    try {
      await api.post("/backups");
      toast.success("Sauvegarde créée");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doImport = async (e, endpoint, ref) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importation : ${data.created} créé(s), ${data.skipped} ignoré(s)`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'importation");
    }
    if (ref.current) ref.current.value = "";
  };

  const restore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      if (!payload.collections) {
        toast.error("Fichier de sauvegarde invalide");
      } else {
        setPendingRestore(payload);
      }
    } catch (err) {
      toast.error("Fichier de sauvegarde invalide");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const confirmRestore = async () => {
    try {
      await api.post("/restore", { ...pendingRestore, confirm: true });
      toast.success("Restauration terminée (une sauvegarde de sécurité a été créée)");
      setPendingRestore(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de la restauration");
      setPendingRestore(null);
    }
  };

  if (loading || !settings) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div data-testid="parametres-page">
      <PageHeader title="Paramètres" subtitle="Configuration de l'organisme, sauvegarde et journal" />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="organisme" data-testid="tab-organisme"><Building2 className="mr-1.5 h-4 w-4" /> Organisme</TabsTrigger>
          <TabsTrigger value="taux" data-testid="tab-taux"><Percent className="mr-1.5 h-4 w-4" /> Taux Acte Générateur</TabsTrigger>
          <TabsTrigger value="sauvegarde" data-testid="tab-sauvegarde"><DatabaseBackup className="mr-1.5 h-4 w-4" /> Sauvegarde</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Importation</TabsTrigger>
          <TabsTrigger value="relances" data-testid="tab-relances" onClick={loadReminders}><Mail className="mr-1.5 h-4 w-4" /> Relances</TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal"><History className="mr-1.5 h-4 w-4" /> Journal</TabsTrigger>
        </TabsList>

        <TabsContent value="organisme">
          <Card className="border-slate-200 p-6 shadow-sm">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Nom de l'organisme</Label>
                <Input value={settings.organisme || ""} onChange={(e) => setField("organisme", e.target.value)} data-testid="set-organisme" />
              </div>
              <div className="space-y-1.5">
                <Label>Sigle</Label>
                <Input value={settings.sigle || ""} onChange={(e) => setField("sigle", e.target.value)} data-testid="set-sigle" />
              </div>
              <div className="space-y-1.5">
                <Label>Préfixe N° Note de perception</Label>
                <Input value={settings.prefixe_note || ""} onChange={(e) => setField("prefixe_note", e.target.value)} data-testid="set-prefixe" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Adresse</Label>
                <Input value={settings.adresse || ""} onChange={(e) => setField("adresse", e.target.value)} data-testid="set-adresse" />
              </div>
              <div className="space-y-1.5">
                <Label>Boîte postale</Label>
                <Input value={settings.bp || ""} onChange={(e) => setField("bp", e.target.value)} data-testid="set-bp" />
              </div>
              <div className="space-y-1.5">
                <Label>Téléphone</Label>
                <Input value={settings.telephone || ""} onChange={(e) => setField("telephone", e.target.value)} data-testid="set-tel" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input value={settings.email || ""} onChange={(e) => setField("email", e.target.value)} data-testid="set-email" />
              </div>
              <div className="space-y-1.5">
                <Label>Site web</Label>
                <Input value={settings.site_web || ""} onChange={(e) => setField("site_web", e.target.value)} data-testid="set-web" />
              </div>
            </div>

            <div className="mt-6">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Comptes bancaires (Banque de paiement)</p>
                <Button variant="outline" size="sm" onClick={addBanque} data-testid="add-banque-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Ajouter</Button>
              </div>
              <div className="space-y-2">
                {(settings.banques || []).map((b, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <Input className="col-span-4" placeholder="Banque" value={b.banque} onChange={(e) => setBanque(i, "banque", e.target.value)} />
                    <Input className="col-span-5 font-mono text-xs" placeholder="N° de compte" value={b.compte} onChange={(e) => setBanque(i, "compte", e.target.value)} />
                    <Input className="col-span-2" placeholder="Monnaie" value={b.monnaie} onChange={(e) => setBanque(i, "monnaie", e.target.value)} />
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeBanque(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <Button onClick={saveSettings} className="bg-slate-900 hover:bg-slate-800" data-testid="save-settings-btn">
                <Save className="mr-1.5 h-4 w-4" /> Enregistrer les paramètres
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="taux">
          <Card className="border-slate-200 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="font-heading text-base font-semibold text-slate-800">Taux des Actes Générateurs</h3>
                <p className="text-xs text-slate-500">Définissez le taux applicable à chaque acte générateur.</p>
              </div>
              <Button onClick={() => openTaux(null)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-taux-btn">
                <Plus className="mr-1.5 h-4 w-4" /> Ajouter un taux
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Acte Générateur</TableHead><TableHead>Taux</TableHead><TableHead>Code</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taux.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="py-8 text-center text-slate-400">Aucun taux défini</TableCell></TableRow>
                ) : taux.map((t) => (
                  <TableRow key={t.id} className="hover:bg-slate-50" data-testid="taux-row">
                    <TableCell className="font-medium text-slate-800">{t.acte_generateur_nom}</TableCell>
                    <TableCell className="font-mono">{t.taux}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{t.code || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openTaux(t)} data-testid="edit-taux-btn"><Pencil className="h-4 w-4 text-slate-500" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteTaux(t.id)} data-testid="delete-taux-btn"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="sauvegarde">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Card className="border-slate-200 p-6 shadow-sm">
              <div className="mb-2 flex items-center gap-2"><Download className="h-5 w-5 text-slate-600" /><h3 className="font-heading text-base font-semibold text-slate-800">Sauvegarde des données</h3></div>
              <p className="mb-4 text-sm text-slate-500">Téléchargez une copie complète de toutes les données (assujettis, notes, utilisateurs, paramètres) au format JSON.</p>
              <Button onClick={downloadBackup} className="bg-slate-900 hover:bg-slate-800" data-testid="download-backup-btn">
                <Download className="mr-1.5 h-4 w-4" /> Télécharger la sauvegarde
              </Button>
            </Card>
            <Card className="border-slate-200 p-6 shadow-sm">
              <div className="mb-2 flex items-center gap-2"><Upload className="h-5 w-5 text-amber-600" /><h3 className="font-heading text-base font-semibold text-slate-800">Restauration</h3></div>
              <p className="mb-4 text-sm text-slate-500">Restaurez les données à partir d'un fichier de sauvegarde. <span className="font-semibold text-red-600">Attention : cela remplace les données actuelles.</span></p>
              <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={restore} data-testid="restore-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="restore-btn">
                <Upload className="mr-1.5 h-4 w-4" /> Importer une sauvegarde
              </Button>
            </Card>
          </div>

          <Card className="mt-6 border-slate-200 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="font-heading text-base font-semibold text-slate-800">Sauvegardes automatiques</h3>
                <p className="text-xs text-slate-500">Générées chaque jour à 02h00 (UTC). Les 30 dernières sont conservées.</p>
              </div>
              <Button variant="outline" size="sm" onClick={createBackupNow} data-testid="create-backup-btn">
                <RotateCw className="mr-1.5 h-4 w-4" /> Sauvegarder maintenant
              </Button>
            </div>
            <div className="max-h-[360px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Date</TableHead><TableHead>Type</TableHead>
                    <TableHead>Taille</TableHead><TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="py-8 text-center text-slate-400">Aucune sauvegarde automatique pour l'instant</TableCell></TableRow>
                  ) : backups.map((b) => (
                    <TableRow key={b.id} className="hover:bg-slate-50" data-testid="backup-row">
                      <TableCell className="text-xs text-slate-600">{fmtDateTime(b.created_at)}</TableCell>
                      <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{b.kind}</span></TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{(b.size / 1024).toFixed(1)} Ko</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => downloadStored(b.id)} data-testid="download-stored-backup-btn">
                          <Download className="mr-1 h-3.5 w-3.5" /> Télécharger
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="import">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Card className="border-slate-200 p-6 shadow-sm">
              <div className="mb-2 flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-600" /><h3 className="font-heading text-base font-semibold text-slate-800">Importer des Assujettis</h3></div>
              <p className="mb-4 text-sm text-slate-500">Fichier Excel (.xlsx) au même format que l'export : Code, Redevable, Acte Générateur, Province, Ville, Commune, Quartier, Avenue, N°, Téléphone, RCCM, N° Impôt.</p>
              <input ref={impAssRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => doImport(e, "/assujettis/import/excel", impAssRef)} data-testid="import-assujettis-input" />
              <Button variant="outline" onClick={() => impAssRef.current?.click()} data-testid="import-assujettis-btn">
                <Upload className="mr-1.5 h-4 w-4" /> Choisir un fichier Excel
              </Button>
            </Card>
            <Card className="border-slate-200 p-6 shadow-sm">
              <div className="mb-2 flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-amber-600" /><h3 className="font-heading text-base font-semibold text-slate-800">Importer des Notes de Perception</h3></div>
              <p className="mb-4 text-sm text-slate-500">Fichier Excel (.xlsx) au format export : N°, Code Assujetti, Redevable, Acte Générateur, Note de Taxation, Date, Mois, Année, Monnaie, Montant. Le redevable est relié par son code.</p>
              <input ref={impNoteRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => doImport(e, "/notes-debit/import/excel", impNoteRef)} data-testid="import-notes-input" />
              <Button variant="outline" onClick={() => impNoteRef.current?.click()} data-testid="import-notes-btn">
                <Upload className="mr-1.5 h-4 w-4" /> Choisir un fichier Excel
              </Button>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="relances">
          <Card className="border-slate-200 p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5 text-amber-600" />
              <h3 className="font-heading text-base font-semibold text-slate-800">Relances de paiement par email</h3>
            </div>
            <p className="mb-4 text-sm text-slate-500">
              Un rappel est envoyé automatiquement le 1er de chaque mois (07h00 UTC) aux redevables ayant un solde impayé
              et une adresse email renseignée. Vous pouvez aussi déclencher l'envoi manuellement.
            </p>

            <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-md border border-slate-200 p-4">
                <p className="text-xs uppercase tracking-wider text-slate-400">Redevables avec solde impayé</p>
                <p className="mt-1 font-mono text-2xl font-bold text-slate-900" data-testid="reminders-count">
                  {reminders ? reminders.count : "—"}
                </p>
              </div>
              <div className="rounded-md border border-slate-200 p-4">
                <p className="text-xs uppercase tracking-wider text-slate-400">Avec adresse email</p>
                <p className="mt-1 font-mono text-2xl font-bold text-emerald-700" data-testid="reminders-with-email">
                  {reminders ? reminders.avec_email : "—"}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={loadReminders} data-testid="refresh-reminders-btn">
                <RotateCw className="mr-1.5 h-4 w-4" /> Actualiser
              </Button>
              <Button variant="outline" onClick={() => sendReminders(true)} disabled={sending} data-testid="send-test-reminder-btn">
                <Send className="mr-1.5 h-4 w-4" /> Envoyer un email de test
              </Button>
              <Button onClick={() => sendReminders(false)} disabled={sending} className="bg-slate-900 hover:bg-slate-800" data-testid="send-reminders-btn">
                {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />}
                Envoyer les relances maintenant
              </Button>
            </div>

            {reminders && reminders.items.length > 0 && (
              <div className="mt-5 max-h-[360px] overflow-y-auto rounded-md border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Redevable</TableHead><TableHead>Email</TableHead><TableHead className="text-right">Solde(s) dû(s)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reminders.items.map((it, i) => (
                      <TableRow key={`${it.redevable}-${i}`} className="hover:bg-slate-50" data-testid="reminder-row">
                        <TableCell className="font-medium text-slate-800">{it.redevable}</TableCell>
                        <TableCell className={`text-xs ${it.email ? "text-slate-600" : "text-red-500"}`}>{it.email || "Aucun email"}</TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {Object.entries(it.soldes).map(([cur, v]) => (
                            <div key={cur}>{new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2 }).format(v.solde)} {cur}</div>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="journal">
          <Card className="border-slate-200 shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4"><h3 className="font-heading text-base font-semibold text-slate-800">Journal des opérations</h3></div>
            <div className="max-h-[560px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Utilisateur</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entité</TableHead>
                    <TableHead>Détails</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {audit.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="py-10 text-center text-slate-400">Aucune opération enregistrée</TableCell></TableRow>
                  ) : audit.map((l) => (
                    <TableRow key={l.id} className="hover:bg-slate-50" data-testid="audit-row">
                      <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmtDateTime(l.timestamp)}</TableCell>
                      <TableCell className="text-sm text-slate-700">{l.user_name || "—"}</TableCell>
                      <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{l.action}</span></TableCell>
                      <TableCell className="text-sm text-slate-600">{l.entity}</TableCell>
                      <TableCell className="text-xs text-slate-500">{l.details || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={tauxDialog} onOpenChange={setTauxDialog}>
        <DialogContent data-testid="taux-dialog">
          <DialogHeader>
            <DialogTitle>{editingTaux ? "Modifier le taux" : "Nouveau taux"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Acte Générateur *</Label>
              <Select value={tauxForm.acte_generateur_id} onValueChange={(v) => setTauxForm({ ...tauxForm, acte_generateur_id: v })}>
                <SelectTrigger data-testid="taux-acte"><SelectValue placeholder="Sélectionner un acte" /></SelectTrigger>
                <SelectContent>
                  {actes.map((a) => <SelectItem key={a.id} value={a.id}>{a.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Taux *</Label>
                <Input type="number" step="0.01" value={tauxForm.taux} onChange={(e) => setTauxForm({ ...tauxForm, taux: e.target.value })}
                  placeholder="Ex: 15.5" className="font-mono" data-testid="taux-value" />
              </div>
              <div className="space-y-1.5">
                <Label>Code</Label>
                <Input value={tauxForm.code} onChange={(e) => setTauxForm({ ...tauxForm, code: e.target.value })}
                  placeholder="Ex: TX-001" data-testid="taux-code" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTauxDialog(false)}>Annuler</Button>
            <Button onClick={saveTaux} className="bg-slate-900 hover:bg-slate-800" data-testid="save-taux-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingRestore}
        onOpenChange={(o) => !o && setPendingRestore(null)}
        title="Confirmer la restauration ?"
        description="Les données actuelles seront remplacées par le contenu du fichier. Une sauvegarde de sécurité automatique sera créée au préalable et restera téléchargeable."
        onConfirm={confirmRestore}
        confirmLabel="Restaurer"
      />
    </div>
  );
}
